'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedProgressCircle } from '@/components/ui/animated-progress-circle';
import type { IconSvgElement } from '@/components/ui/icon';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIcon, TelegramIcon } from '@/components/ui/icon/brands';
import { CheckmarkCircle02Icon, Edit02Icon, Mail02Icon } from '@/components/ui/icon/glyphs';
import { Panel, PanelHeader, PanelTitle } from '@/components/ui/panel';
import { StatusPill } from '@/components/ui/status-pill';
import { TOAST_IDS } from '@/config/toast-ids';
import { confettiOriginFromElement } from '@/lib/ui/confetti';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { InfoTipModal, useRewardsTips } from './info-tip';
import { PointsChip } from './points-chip';
import type { QuestVerification } from './use-quest-verification';
interface Quest {
  id: string;
  title: string;
  description: string | null;
  points: number;
  verifyMethod: number;
  ctaMode: string;
  actionUrl: string;
  isNew: boolean;
  status: number;
  assurance: number;
}
interface QuestsPanelProps {
  quests: Quest[];
  verify: QuestVerification;
  /** Return true to fully handle a quest CTA (e.g. open a modal) instead of
   *  the default open-action/verify flow. */
  onQuestAction?: (quest: Quest) => boolean;
  className?: string;
}

/** X glyph for X quests, Telegram for connect (4), pencil for content (5/7),
 *  mail for the add-email quest (11). */
function questIcon(quest: Quest): IconSvgElement {
  if (quest.verifyMethod === 4) return TelegramIcon;
  if (quest.verifyMethod === 5 || quest.verifyMethod === 7) return Edit02Icon;
  if (quest.verifyMethod === 11) return Mail02Icon;
  return NewTwitterIcon;
}

/**
 * One quest row + its verification state machine:
 *   completed (server status 1) → ✓ pill (terminal)
 *   idle      → "Complete" (opens the action, then verifies on return)
 *   verifying → "Checking…" while we poll the X API on return
 *   unverified→ hint + "Re-verify" + "Do it again" (entry point back to the action)
 *   unavailable→ "verification is down, your action is safe" hint + "Re-verify"
 *   private   → "your X account is private" hint + "Re-verify"
 */
function QuestRow(t0) {
  const $ = _c(41);
  const {
    quest,
    verify,
    onQuestAction
  } = t0;
  const t = useTranslations("rewards");
  const done = quest.status === 1;
  let t1;
  if ($[0] !== quest.id || $[1] !== verify) {
    t1 = verify.phaseOf(quest.id);
    $[0] = quest.id;
    $[1] = verify;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const phase = t1;
  const failed = !done && (phase === "unverified" || phase === "unavailable" || phase === "private" || phase === "restricted");
  let t2;
  if ($[3] !== phase || $[4] !== t) {
    t2 = phase === "unavailable" ? t("quests.unavailableHint") : phase === "private" ? t("quests.privateAccountHint") : phase === "restricted" ? t("quests.restrictedAccountHint") : t("quests.notVerifiedHint");
    $[3] = phase;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const failureHint = t2;
  const t3 = `quest-${quest.id}`;
  const t4 = done ? "verified" : phase;
  let t5;
  if ($[6] !== quest) {
    t5 = questIcon(quest);
    $[6] = quest;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-background-secondary"><Icon icon={t5} className="size-4 text-foreground-secondary" /></span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== quest.title) {
    t7 = <span className="truncate text-body font-medium text-foreground-primary">{quest.title}</span>;
    $[10] = quest.title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== failed || $[13] !== failureHint || $[14] !== quest.description) {
    t8 = failed ? <p className="truncate text-caption text-feedback-warning-content">{failureHint}</p> : quest.description !== null ? <p className="truncate text-caption text-foreground-tertiary">{quest.description}</p> : null;
    $[12] = failed;
    $[13] = failureHint;
    $[14] = quest.description;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t7}{t8}</div>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== quest.points || $[20] !== t) {
    t10 = t("quests.pts", {
      points: quest.points
    });
    $[19] = quest.points;
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <PointsChip>{t10}</PointsChip>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t6 || $[26] !== t9) {
    t12 = <div className="flex min-w-0 items-center gap-3 @md/quests:flex-1">{t6}{t9}{t11}</div>;
    $[24] = t11;
    $[25] = t6;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== done || $[29] !== failed || $[30] !== onQuestAction || $[31] !== phase || $[32] !== quest || $[33] !== t || $[34] !== verify) {
    t13 = <div className="flex items-center justify-end gap-1.5 @md/quests:shrink-0">{done ? <StatusPill tone="success" size="sm"><Icon icon={CheckmarkCircle02Icon} className="size-3.5" />{quest.assurance === 0 ? t("quests.completed") : t("quests.verified")}</StatusPill> : phase === "verifying" ? <AnimatedButton size="sm" disabled={true} loading={true} data-testid={`quest-${quest.id}-checking`} className="flex-1 @md/quests:flex-none">{t("quests.verifying")}</AnimatedButton> : failed ? <>{phase === "unverified" && <AnimatedButton size="sm" variant="tertiary" onClick={e => verify.redo(quest, confettiOriginFromElement(e.currentTarget))}>{t("quests.doItAgain")}</AnimatedButton>}<AnimatedButton size="sm" data-testid={`quest-${quest.id}-reverify`} className="flex-1 @md/quests:flex-none" onClick={e_0 => verify.reverify(quest.id, confettiOriginFromElement(e_0.currentTarget))}>{t("quests.reverify")}</AnimatedButton></> : <AnimatedButton size="sm" data-testid={`quest-${quest.id}-cta`} className="flex-1 @md/quests:flex-none" onClick={e_1 => {
        if (onQuestAction?.(quest) === true) {
          return;
        }
        verify.start(quest, confettiOriginFromElement(e_1.currentTarget));
      }}>{quest.verifyMethod === 4 ? t("quests.ctaConnect") : t("quests.doIt")}</AnimatedButton>}</div>;
    $[28] = done;
    $[29] = failed;
    $[30] = onQuestAction;
    $[31] = phase;
    $[32] = quest;
    $[33] = t;
    $[34] = verify;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t3 || $[39] !== t4) {
    t14 = <div data-testid={t3} data-phase={t4} className="flex flex-col gap-2 rounded-panel-row px-2 py-2.5 hover:bg-background-tertiary @md/quests:flex-row @md/quests:items-center @md/quests:gap-3">{t12}{t13}</div>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t3;
    $[39] = t4;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
}
export function QuestsPanel(t0) {
  const $ = _c(35);
  const {
    quests,
    verify,
    onQuestAction,
    className
  } = t0;
  const t = useTranslations("rewards");
  const tips = useRewardsTips();
  const incomplete = quests.filter(_temp);
  const completed = quests.filter(_temp2);
  const completedPct = quests.length > 0 ? completed.length / quests.length * 100 : 0;
  const scrollRef = useRef(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const completedSeenRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== quests || $[1] !== t) {
    t1 = () => {
      const completedNow = new Set();
      for (const q_1 of quests) {
        if (q_1.status === 1) {
          completedNow.add(q_1.id);
        }
      }
      const prev = completedSeenRef.current;
      completedSeenRef.current = completedNow;
      if (prev === null) {
        return;
      }
      const justCompleted = quests.find(q_2 => q_2.status === 1 && !prev.has(q_2.id));
      if (justCompleted === undefined) {
        return;
      }
      showToast({
        id: TOAST_IDS.REWARDS_QUEST_COMPLETED,
        message: t("quests.completedToast", {
          points: justCompleted.points
        }),
        description: justCompleted.title,
        variant: "success"
      });
    };
    t2 = [quests, t];
    $[0] = quests;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = scrollRef.current;
      if (el === null) {
        return;
      }
      const update = () => {
        setShowTopFade(el.scrollTop > 0);
        setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
      };
      const observer = new ResizeObserver(update);
      observer.observe(el);
      el.addEventListener("scroll", update, {
        passive: true
      });
      return () => {
        observer.disconnect();
        el.removeEventListener("scroll", update);
      };
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== className) {
    t5 = cn("gap-3 relative lg:overflow-hidden", className);
    $[6] = className;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("quests.title");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== tips.quests) {
    t7 = <InfoTipModal tip={tips.quests} />;
    $[10] = tips.quests;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <span className="flex items-center gap-1.5">{t6}{t7}</span>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== completedPct) {
    t9 = <AnimatedProgressCircle progress={completedPct} diameter={20} strokeWidth={4} className="shrink-0" />;
    $[15] = completedPct;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = "relative flex flex-col lg:min-h-0 lg:flex-1";
  const t11 = "panel-content";
  const t12 = "@container/quests flex flex-col gap-1 px-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto";
  let t13;
  if ($[17] !== onQuestAction || $[18] !== verify) {
    t13 = q_3 => <QuestRow key={q_3.id} quest={q_3} verify={verify} onQuestAction={onQuestAction} />;
    $[17] = onQuestAction;
    $[18] = verify;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = incomplete.map(t13);
  const t15 = completed.length > 0 ? <><p className="px-2 pt-2 text-caption font-medium text-foreground-tertiary">{t("quests.completedSection")}</p>{completed.map(q_4 => <QuestRow key={q_4.id} quest={q_4} verify={verify} />)}</> : null;
  let t16;
  if ($[20] !== t14 || $[21] !== t15) {
    t16 = <div ref={scrollRef} data-slot={t11} className={t12}>{t14}{t15}</div>;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const t17 = showTopFade ? "opacity-100" : "opacity-0";
  let t18;
  if ($[23] !== t17) {
    t18 = cn("pointer-events-none absolute inset-x-0 top-0 hidden h-6 bg-gradient-to-b from-background-primary to-transparent transition-opacity duration-normal lg:block", t17);
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t18) {
    t19 = <div aria-hidden={true} className={t18} />;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = showBottomFade ? "opacity-100" : "opacity-0";
  let t21;
  if ($[27] !== t20) {
    t21 = cn("pointer-events-none absolute inset-x-0 bottom-0 hidden h-6 bg-gradient-to-t from-background-primary to-transparent transition-opacity duration-normal lg:block", t20);
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== t21) {
    t22 = <div aria-hidden={true} className={t21} />;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== t16 || $[32] !== t19 || $[33] !== t22) {
    t23 = <div className={t10}>{t16}{t19}{t22}</div>;
    $[31] = t16;
    $[32] = t19;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  return <Panel data-testid="quests-panel" className={t5}><PanelHeader><PanelTitle className="flex items-center justify-between gap-2 text-body-lg">{t8}<span className="flex items-center gap-1.5"><span className="text-caption font-medium tabular-nums text-foreground-secondary">{completed.length}/{quests.length}</span>{t9}</span></PanelTitle></PanelHeader>{t23}</Panel>;
}
function _temp2(q_0) {
  return q_0.status === 1;
}
function _temp(q) {
  return q.status !== 1;
}